/** Scanner filters aligned with watchlist verdicts + ranking. */

import { resolveDisplayVerdict } from "./resolveDisplayVerdict.js";
import { mapScannerActionToDisplay } from "./resolveScannerAction.js";
import { watchlistConfluenceCell } from "./confluenceScore.js";
import { evaluateNearPrimeSetup } from "./primeSetupFlag.js";
import { isScoringV2Result } from "./resolveScannerAction.js";

export const SCANNER_PRESETS = {
  SWING_LONGS: {
    label: "Swing longs",
    quickFilter: "CONSIDER",
    verdictProfile: "CONSIDER_CALLS",
    validation: { cmf: true, rsi: false, volume: false, obv: false, preFilter: false },
    context: { minPrice5: true, excludeEarnings14: true, bullTide: false, qqqBullish: false, sectorLeaders: false },
    minStrength: 0,
  },
  BREAKOUTS: {
    label: "Breakouts",
    quickFilter: "ALL",
    verdictProfile: "CONFLUENCE_BREAK",
    validation: { cmf: false, rsi: false, volume: true, obv: false, preFilter: false },
    context: { minPrice5: true, excludeEarnings14: false, bullTide: false, qqqBullish: false, sectorLeaders: false },
    minStrength: 0,
  },
  PUTS: {
    label: "Puts",
    quickFilter: "TOP",
    verdictProfile: "PUTS",
    validation: { cmf: true, rsi: false, volume: false, obv: false, preFilter: false },
    context: { minPrice5: true, excludeEarnings14: false, bullTide: false, qqqBullish: false, sectorLeaders: false },
    minStrength: 0,
  },
  ROTATION: {
    label: "Rotation leaders",
    quickFilter: "CONSIDER",
    verdictProfile: "CONSIDER_CALLS",
    validation: { cmf: true, rsi: false, volume: false, obv: false, preFilter: false },
    context: { minPrice5: true, excludeEarnings14: true, bullTide: false, qqqBullish: false, sectorLeaders: true },
    minStrength: 0,
  },
  PRIME: {
    label: "Prime",
    quickFilter: "PRIME",
    verdictProfile: "PRIME",
    validation: { cmf: true, rsi: false, volume: false, obv: false, preFilter: false },
    context: { minPrice5: true, excludeEarnings14: true, bullTide: false, qqqBullish: false, sectorLeaders: false },
    minStrength: 0,
  },
};

export function applyScannerPreset(presetId) {
  const p = SCANNER_PRESETS[presetId];
  if (!p) return null;
  return {
    quickFilter: p.quickFilter,
    verdictProfile: p.verdictProfile,
    validation: { ...p.validation },
    context: { ...p.context },
    minStrength: p.minStrength,
    signalTypes: ["ALL"],
    setupTypes: [],
  };
}

export function scannerDisplayVerdict(result) {
  return resolveDisplayVerdict(result, {
    inPosition: !!result?.inPosition,
    activeSignal: result?.activeSignal,
  });
}

export function isExtendedVerdict(result) {
  const d = scannerDisplayVerdict(result);
  return d.verdict === "⚠ WAIT — EXTENDED" || /EXTENDED/i.test(d.verdict);
}

export function isPrimeScannerSetup(result) {
  if (!result || isExtendedVerdict(result)) return false;
  if (typeof result.isPrime === "boolean") return result.isPrime;
  const d = scannerDisplayVerdict(result);
  const gateOk = result.gate?.label === "GO" && !result.gate?.blocked;
  const scoreOk = typeof result.buyScore === "number" && result.buyScore >= 6;
  const cmfOk = result.cmf != null && result.cmf > 0.05;
  const callsOk = result.callsSignal?.fullSignal === true;
  return gateOk && scoreOk && cmfOk && (callsOk || d.verdict === "⚡ CONSIDER CALLS");
}

/** Higher = better swing-long setup (used for Rank / Verdict column sort). */
export function rankScannerResult(result) {
  if (!result) return -999;
  if (isScoringV2Result(result) && result.scannerAction?.sortRank != null) {
    let rank = result.scannerAction.sortRank;
    if (typeof result.buyScore === "number") rank += result.buyScore / 100;
    const g = result.momentum_grade ?? result.puts_grade;
    if (g === "A") rank += 0.35;
    else if (g === "B") rank += 0.2;
    else if (g === "C") rank += 0.05;
    if (result.isPrime === true) rank += 5;
    return rank;
  }
  let rank = typeof result.buyScore === "number" ? result.buyScore : 0;
  const d = scannerDisplayVerdict(result);
  const v = d.verdict;

  if (result.isPrime === true) rank += 10;

  if (v === "⚡ CONSIDER CALLS") rank += 8;
  else if (v === "👁 WATCH" && !/BEARISH/i.test(v)) rank += 3;
  else if (v === "👁 WATCH — BEARISH") rank -= 3;
  else if (v === "🔵 BOTTOM CONFIRMED") rank += 1;
  else if (v === "⚠ CONSIDER PUTS" || v === "⚠ TOP FORMING") rank -= 10;
  else if (v === "⚪ WAIT") rank -= 5;

  if (isExtendedVerdict(result)) rank -= 12;

  const modSig = result?.signal;
  if (typeof modSig === "string") {
    if (/TOP_FORMING|TOP_CONFIRMED/i.test(modSig) && !/CONSIDER PUTS|TOP FORMING/.test(v)) {
      rank -= 8;
    }
    if (
      /BOTTOM_FORMING/i.test(modSig)
      && v !== "⚡ CONSIDER CALLS"
      && v !== "🔵 BOTTOM CONFIRMED"
    ) {
      rank -= 9;
    }
  }

  if (result.gate?.label === "GO") rank += 4;
  else if (result.gate?.blocked) rank -= 6;
  if (result.callsSignal?.fullSignal) rank += 3;
  const cf = watchlistConfluenceCell(result);
  if (cf?.text && cf.text !== "—") rank += 2;
  if (typeof result.signalStrength === "number") rank += result.signalStrength / 40;
  return rank;
}

/** Same as rankScannerResult — explicit name for UI copy. */
export function verdictQualityRank(result) {
  return rankScannerResult(result);
}

export function matchesVerdictProfile(result, profile) {
  if (!profile || profile === "ALL") return true;
  const d = scannerDisplayVerdict(result);
  if (profile === "CONSIDER_CALLS") {
    const mom = result.momentumChecklist?.status;
    return (
      d.verdict === "⚡ CONSIDER CALLS"
      || /CALLS — ENTRY OK/i.test(d.verdict || "")
      || mom === "ENTRY_OK"
      || mom === "SETUP"
      || (typeof result.buyScore === "number" && result.buyScore >= 6 && !isExtendedVerdict(result))
    );
  }
  if (profile === "CALLS_READY") {
    return result.callsSignal?.fullSignal === true && !result.catchingKnivesCallsBlock;
  }
  if (profile === "PRIME") return isPrimeScannerSetup(result);
  if (profile === "PUTS") {
    return result.putsSignal?.fullSignal === true
      || (typeof result.buyScore === "number" && result.buyScore <= -6);
  }
  if (profile === "CONFLUENCE_BREAK") {
    const cf = watchlistConfluenceCell(result);
    return cf?.text === "BREAK ⚡" || result.volBreakout?.volConfirmed === true;
  }
  if (profile === "NO_EXTENDED") return !isExtendedVerdict(result);
  return true;
}

export function pickTopScannerPicks(results, n = 3, watchlistSymbols = []) {
  const wl = new Set((watchlistSymbols || []).map((s) => String(s).toUpperCase()));
  return [...results]
    .filter(({ symbol, result }) => result && !wl.has(String(symbol).toUpperCase()))
    .sort((a, b) => rankScannerResult(b.result) - rankScannerResult(a.result))
    .slice(0, n);
}

/** Signal-of-the-day payload for watchlist banner. */
export function buildSignalOfDayFromPick({ symbol, result }) {
  if (!symbol || !result) return null;
  const d = scannerDisplayVerdict(result);
  let reason = d.subtitle || result?.scannerAction?.reason || "";
  if (isScoringV2Result(result) && result?.isPrime !== true) {
    const np = evaluateNearPrimeSetup(result);
    if (np.nearPrime && np.missing?.length) {
      const hint = `Near Prime — ${np.missing.slice(0, 2).join("; ")}`;
      reason = reason ? `${reason} · ${hint}` : hint;
    }
  }
  return {
    symbol,
    result,
    score: result.buyScore,
    verdict: {
      short: d.verdict,
      color: d.verdictStyle === "bullish" ? "#16a34a" : d.verdictStyle === "warning" ? "#f59e0b" : "#94a3b8",
      darkColor: "#86efac",
      bg: "#16a34a18",
      darkBg: "#16a34a22",
      reason,
    },
  };
}
